/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useAuth from '../../../hooks/useAuth'
import useModals from '../../../hooks/useModals'
import {POST} from '../../../services/api'
import styles from './cashbackconfig.module.css'

type CashbackTransactionType = 'earn' | 'redeem' | 'reversal'
type BenefitKind = 'percent' | 'fixed'
type ModalMode = 'detail' | 'reversal' | 'create'

type CashbackTransaction = {
  id: number
  customerId: number
  orderId: number | null
  type: CashbackTransactionType
  customerCashback: CashbackCustomer
  amount: number
  description: string | null
  pluginspaceId: number
  customerCashbackId: number | null
  created: string
  updated?: string
}

type CashbackCustomer = {
  balance: number
  customer: Customer
}

type Customer = {
  name: string
}

type CashbackTransactionRow = {
  id: number
  customerId: number
  orderId: string
  orderIdRaw: number | null
  type: string
  typeRaw: CashbackTransactionType
  amount: string
  amountRaw: number
  description: string
  customerCashbackId: string
  customerName: string
  balance: string
  pluginspaceId: string
  created: string
  updated: string
}

type OrderSearchItem = {
  id: number
  orderidExt: string
  customerId: number
  customerName: string
  totalCents: number
  totalReais: number
  totalDisplay: string
  created: string
  statusName: string
}

type FilterState = {
  customerId: string
  orderId: string
  type: string
  amount: string
  description: string
  customerCashbackId: string
  createdFrom: string
  createdTo: string
}

const TYPE_OPTIONS = [
  {value: '', select: 'Todos'},
  {value: 'earn', select: 'Acúmulo'},
  {value: 'redeem', select: 'Resgate'},
  {value: 'reversal', select: 'Estorno'},
]

const TYPE_LABELS: Record<CashbackTransactionType, string> = {
  earn: 'Acúmulo',
  redeem: 'Resgate',
  reversal: 'Estorno',
}

const CREATE_CASHBACK_PROFILES = ['admin', 'owner', 'manager']

const INITIAL_FILTERS: FilterState = {
  customerId: '',
  orderId: '',
  type: '',
  amount: '',
  description: '',
  customerCashbackId: '',
  createdFrom: '',
  createdTo: '',
}

const formatMoney = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '-'
  return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(value))
}

const roundMoney = (value: number) => Math.round(value * 100) / 100

const translateType = (type: string) => {
  return TYPE_LABELS[type as CashbackTransactionType] || type || '-'
}

const buildFilterPayload = (filters: FilterState) => {
  const payload: Record<string, string | number> = {}

  if (filters.customerId) payload.customerId = Number(filters.customerId)
  if (filters.orderId) payload.orderId = Number(filters.orderId)
  if (filters.type) payload.type = filters.type
  if (filters.amount) payload.amount = Number(filters.amount)
  if (filters.description.trim()) payload.description = filters.description.trim()
  if (filters.customerCashbackId) {
    payload.customerCashbackId = Number(filters.customerCashbackId)
  }
  if (filters.createdFrom) {
    payload.createdFrom = moment(filters.createdFrom).startOf('day').toISOString()
  }
  if (filters.createdTo) {
    payload.createdTo = moment(filters.createdTo).endOf('day').toISOString()
  }

  return payload
}

const mapTransactions = (data: CashbackTransaction[]): CashbackTransactionRow[] => {
  return data.map((item) => ({
    id: item.id,
    customerId: item.customerId,
    orderId: item.orderId ? String(item.orderId) : '-',
    orderIdRaw: item.orderId,
    type: translateType(item.type),
    typeRaw: item.type,
    amount: formatMoney(item.amount),
    amountRaw: Number(item.amount || 0),
    description: item.description || '-',
    customerCashbackId: item.customerCashbackId ? String(item.customerCashbackId) : '-',
    customerName: item.customerCashback?.customer?.name || '-',
    balance: formatMoney(item.customerCashback?.balance),
    pluginspaceId: item.pluginspaceId ? String(item.pluginspaceId) : '-',
    created: item.created ? moment(item.created).utcOffset(-3).format('DD/MM/YYYY HH:mm:ss') : '-',
    updated: item.updated ? moment(item.updated).utcOffset(-3).format('DD/MM/YYYY HH:mm:ss') : '-',
  }))
}

const normalizeOrderSearchItem = (order: any): OrderSearchItem => {
  const totalCents = Number(order?.totalWithoutDelivery || 0)
  const totalReais = totalCents / 100
  const statusName = Array.isArray(order?.status)
    ? order.status.find((status: any) => status.id === order.statusId)?.name || '-'
    : '-'

  return {
    id: Number(order?.id || 0),
    orderidExt: String(order?.orderidExt || order?.id || '-'),
    customerId: Number(order?.customer?.id || order?.customerId || 0),
    customerName: order?.customer?.name || '-',
    totalCents,
    totalReais,
    totalDisplay: formatMoney(totalReais),
    created: order?.created
      ? moment(order.created).utcOffset(-3).format('DD/MM/YYYY HH:mm:ss')
      : '-',
    statusName,
  }
}

const CashbackListContent: FC = () => {
  const {loginInfo} = useAuth()
  const {showWrapperModal, closeWrapperModal, wrapperModal} = useModals()
  const canCreateCashback = CREATE_CASHBACK_PROFILES.includes(loginInfo?.profile || '')

  const [loading, setLoading] = useState(false)
  const [savingAction, setSavingAction] = useState(false)
  const [transactions, setTransactions] = useState<CashbackTransactionRow[]>([])
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS)
  const [modalMode, setModalMode] = useState<ModalMode | null>(null)
  const [selectedTransaction, setSelectedTransaction] = useState<CashbackTransactionRow | null>(
    null
  )
  const [reversalDescription, setReversalDescription] = useState('')
  const [descriptionError, setDescriptionError] = useState('')

  const [orderSearchQuery, setOrderSearchQuery] = useState('')
  const [orderSearchLoading, setOrderSearchLoading] = useState(false)
  const [orderSearchResults, setOrderSearchResults] = useState<OrderSearchItem[]>([])
  const [selectedOrder, setSelectedOrder] = useState<OrderSearchItem | null>(null)
  const [benefitKind, setBenefitKind] = useState<BenefitKind>('percent')
  const [benefitValue, setBenefitValue] = useState<number | string>(0)
  const [createDescription, setCreateDescription] = useState('')
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({})

  const setFilterField = <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
    setFilters((prev) => ({...prev, [field]: value}))
  }

  const loadTransactions = async (currentFilters: FilterState = filters) => {
    setLoading(true)
    try {
      const response = await POST(
        '/cashback/transaction/filter',
        buildFilterPayload(currentFilters)
      )

      const dataArray = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : []

      setTransactions(mapTransactions(dataArray))
    } catch (error) {
      console.error(error)
      setTransactions([])
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Erro ao carregar as movimentações de cashback.',
      })
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS)
    loadTransactions(INITIAL_FILTERS)
  }

  const resetModalState = () => {
    setModalMode(null)
    setSelectedTransaction(null)
    setReversalDescription('')
    setDescriptionError('')
    setOrderSearchQuery('')
    setOrderSearchLoading(false)
    setOrderSearchResults([])
    setSelectedOrder(null)
    setBenefitKind('percent')
    setBenefitValue(0)
    setCreateDescription('')
    setCreateErrors({})
  }

  const openDetailModal = (item: CashbackTransactionRow) => {
    setSelectedTransaction(item)
    setModalMode('detail')
    showWrapperModal('Detalhes da movimentação')
  }

  const openReversalModal = (item: CashbackTransactionRow) => {
    if (!item.orderIdRaw) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Esta movimentação não possui pedido vinculado para estorno.',
      })
      return
    }

    setSelectedTransaction(item)
    setModalMode('reversal')
    setReversalDescription('')
    setDescriptionError('')
    showWrapperModal('Estornar Cashback do Pedido')
  }

  const openCreateModal = () => {
    setSelectedTransaction(null)
    setReversalDescription('')
    setDescriptionError('')
    setOrderSearchQuery('')
    setOrderSearchLoading(false)
    setOrderSearchResults([])
    setSelectedOrder(null)
    setBenefitKind('percent')
    setBenefitValue(0)
    setCreateDescription('')
    setCreateErrors({})
    setModalMode('create')
    showWrapperModal('Novo Cashback')
  }

  const closeModal = () => {
    closeWrapperModal()
    resetModalState()
  }

  const getCalculatedAmount = () => {
    if (!selectedOrder) return 0

    const numericValue = Number(benefitValue)
    if (Number.isNaN(numericValue) || numericValue <= 0 || selectedOrder.totalReais <= 0) {
      return 0
    }

    if (benefitKind === 'percent') {
      return roundMoney((selectedOrder.totalReais * numericValue) / 100)
    }

    // Valor em dinheiro: converte para % do pedido e calcula o amount enviado ao back
    const percentFromMoney = (numericValue / selectedOrder.totalReais) * 100
    return roundMoney((selectedOrder.totalReais * percentFromMoney) / 100)
  }

  const confirmReversal = async () => {
    if (!selectedTransaction?.orderIdRaw) {
      closeModal()
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Pedido inválido para estorno.',
      })
      return
    }

    const description = reversalDescription.trim()
    if (!description || description.length < 10) {
      setDescriptionError('Informe o motivo do estorno (mínimo 10 caracteres).')
      return
    }

    try {
      setSavingAction(true)

      const response = await POST(`/cashback/reversal/${selectedTransaction.orderIdRaw}`, {
        orderId: selectedTransaction.orderIdRaw,
        description,
      })

      closeModal()
      await loadTransactions()

      if (response?.success === false) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response?.message || 'Não foi possível estornar o cashback.',
        })
        return
      }

      Swal.fire({
        icon: 'success',
        title: 'Estorno realizado',
        text: response?.message || 'O cashback do pedido foi estornado com sucesso.',
      })
    } catch (error) {
      console.error(error)
      closeModal()
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) ? String(error) : 'Não foi possível estornar o cashback do pedido.',
      })
    } finally {
      setSavingAction(false)
    }
  }

  const confirmCreate = async () => {
    const nextErrors: Record<string, string> = {}

    if (!selectedOrder) {
      nextErrors.order = 'Selecione um pedido para continuar.'
    } else if (!selectedOrder.customerId) {
      nextErrors.order = 'O pedido selecionado não possui cliente vinculado.'
    }

    const numericValue = Number(benefitValue)
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      nextErrors.benefitValue = 'Informe um valor válido maior que zero.'
    } else if (benefitKind === 'percent' && numericValue > 99) {
      nextErrors.benefitValue = 'O percentual precisa estar entre 0 e 99.'
    } else if (
      benefitKind === 'fixed' &&
      selectedOrder &&
      numericValue >= selectedOrder.totalReais
    ) {
      nextErrors.benefitValue = 'O valor não pode ser maior ou igual que o total do pedido.'
    }

    setCreateErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    const amount = getCalculatedAmount()
    if (!selectedOrder || amount <= 0) {
      setCreateErrors({benefitValue: 'Não foi possível calcular o valor do cashback.'})
      return
    }

    try {
      setSavingAction(true)

      const response = await POST('/cashback/transaction', {
        customerId: selectedOrder.customerId,
        orderId: selectedOrder.id,
        type: 'earn',
        amount,
        description:
          createDescription.trim() ||
          `Cashback manual do pedido ${selectedOrder.orderidExt || selectedOrder.id}`,
      })

      closeModal()
      await loadTransactions()

      if (response?.success === false) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response?.message || 'Não foi possível registrar o cashback.',
        })
        return
      }

      Swal.fire({
        icon: 'success',
        title: 'Cashback registrado',
        text: response?.message || 'A movimentação de cashback foi registrada com sucesso.',
      })
    } catch (error) {
      console.error(error)
      closeModal()
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) ? String(error) : 'Não foi possível registrar o cashback.',
      })
    } finally {
      setSavingAction(false)
    }
  }

  useEffect(() => {
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (modalMode !== 'create' || !wrapperModal.display) return

    const timer = setTimeout(() => {
      const query = orderSearchQuery.trim()

      if (query.length < 4 || selectedOrder) {
        if (!selectedOrder) setOrderSearchResults([])
        setOrderSearchLoading(false)
        return
      }

      const runSearch = async () => {
        try {
          setOrderSearchLoading(true)

          let response = await POST('/orders/filter-admin_v2?page=1&take=20', {
            orderidExt: query,
          })
          let dataArray = Array.isArray(response?.data) ? response.data : []

          if (dataArray.length === 0 && /^\d+$/.test(query)) {
            response = await POST('/orders/filter-admin_v2?page=1&take=20', {
              orderId: Number(query),
            })
            dataArray = Array.isArray(response?.data) ? response.data : []
          }

          setOrderSearchResults(dataArray.map(normalizeOrderSearchItem))
        } catch (error) {
          console.error('Erro ao buscar pedidos:', error)
          setOrderSearchResults([])
        } finally {
          setOrderSearchLoading(false)
        }
      }

      void runSearch()
    }, 300)

    return () => clearTimeout(timer)
  }, [orderSearchQuery, modalMode, wrapperModal.display, selectedOrder])

  const modalConfirmAction = () => {
    if (modalMode === 'detail') {
      closeModal()
      return
    }
    if (modalMode === 'create') {
      void confirmCreate()
      return
    }
    void confirmReversal()
  }

  const modalTextAction =
    modalMode === 'detail'
      ? 'Fechar'
      : savingAction
      ? modalMode === 'create'
        ? 'Salvando...'
        : 'Estornando...'
      : modalMode === 'create'
      ? 'Salvar'
      : 'Confirmar'

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Movimentações</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Consulte o histórico de acúmulo, resgate e estorno de cashback.
                </span>
              </h3>
              {canCreateCashback && (
                <div className='card-toolbar'>
                  <Button
                    text='Novo Cashback'
                    color='primary'
                    size='small'
                    icon='fas fa-plus'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={openCreateModal}
                    btnClass='fw-bolder'
                  />
                </div>
              )}
            </div>
            <div className='card-body'>
              <div className='w-100 mb-10'>
                <div className='row align-items-end g-5'>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label='Tipo'
                      value={filters.type}
                      change={(value: string) => setFilterField('type', value)}
                      options={TYPE_OPTIONS}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-2'>
                    <Input
                      inputAttr={{type: 'money'}}
                      label='Valor'
                      value={filters.amount}
                      change={(value: string) => setFilterField('amount', value)}
                      placeholder='Valor'
                      marginTop='0'
                      currency='BRL'
                      locale='pt-BR'
                    />
                  </div>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'date'}}
                      label='Data inicial'
                      value={filters.createdFrom}
                      change={(value: string) => setFilterField('createdFrom', value)}
                      maxLength={filters.createdTo}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'date'}}
                      label='Data final'
                      value={filters.createdTo}
                      change={(value: string) => setFilterField('createdTo', value)}
                      minLength={filters.createdFrom}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-12 d-flex justify-content-md-end gap-3'>
                    <div className='d-flex justify-content-md-end gap-3'>
                      <Button
                        color='primary'
                        icon='fa fa-filter'
                        text='Filtrar'
                        size='small'
                        click={() => loadTransactions()}
                        btnClass='mb-0'
                      />
                      <Button
                        color='danger'
                        icon='fa fa-eraser'
                        size='small'
                        text='Limpar'
                        click={clearFilters}
                        btnClass='mb-0'
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Datatable
                data={transactions}
                headers={[
                  {key: 'Pedido', value: 'orderId', type: 'default'},
                  {key: 'Cliente', value: 'customerName', type: 'default'},
                  {
                    key: 'Tipo',
                    value: 'type',
                    type: 'badge',
                    badge: {
                      acúmulo: 'success',
                      resgate: 'warning',
                      estorno: 'danger',
                    },
                  },
                  {key: 'Valor', value: 'amount', type: 'default'},
                  {key: 'Descrição', value: 'description', type: 'default'},
                  {key: 'Data', value: 'created', type: 'default'},
                  {key: 'Ações', value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'bi bi-eye',
                    action: (item) => openDetailModal(item),
                    title: 'Ver detalhes',
                  },
                  {
                    icon: 'bi bi-arrow-counterclockwise',
                    action: (item) => openReversalModal(item),
                    title: 'Estornar cashback',
                    verifyConditionItem: 'type',
                    verifyConditionValue: 'Acúmulo',
                  },
                  {
                    icon: 'bi bi-box-arrow-up-right',
                    action: (item) => {
                      window.open(`/order/saleorder/${item.orderIdRaw}`, '_blank')
                    },
                    title: 'Ver pedido',
                    verifyConditionItem: 'orderIdRaw',
                    verifyConditionValue: (value: number | null) => !!value,
                  },
                ]}
                color='primary'
              />
            </div>
          </div>
        </div>
      )}

      <ModalWrapper
        confirmAction={modalConfirmAction}
        shouldCloseModal={modalMode === 'detail'}
        disableMainButton={savingAction}
        onCloseAction={resetModalState}
        textAction={modalTextAction}
        confirmButtomColor='primary'
        noCloseButton={modalMode === 'detail'}
        width={60}
      >
        {selectedTransaction && modalMode === 'detail' && (
          <div className='row g-4'>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-eye text-primary me-2'></i>
                Detalhes da movimentação
              </h6>
            </div>

            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Pedido</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.orderId}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Cliente</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.customerName}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Tipo</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.type}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Valor</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.amount}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Saldo atual da carteira</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.balance}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Data</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.created}</div>
            </div>
            <div className='col-12 mb-12'>
              <div className='text-muted fs-7 mb-1'>Descrição</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.description}</div>
            </div>
          </div>
        )}

        {selectedTransaction && modalMode === 'reversal' && (
          <div className='row g-4'>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-receipt text-primary me-2'></i>
                Dados da movimentação
              </h6>
            </div>

            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Pedido</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.orderId}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Cliente</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.customerName}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Tipo</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.type}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Valor</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.amount}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Data</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.created}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Descrição</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.description}</div>
            </div>

            <div className='col-12'>
              <div className='separator separator-dashed'></div>
            </div>

            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-chat-left-text text-primary me-2'></i>
                Motivo do estorno
              </h6>
              <Input
                inputAttr={{type: 'textarea'}}
                label='Observação'
                placeholder='Informe o motivo do estorno deste cashback'
                value={reversalDescription}
                change={(value: string) => {
                  setReversalDescription(value)
                  if (descriptionError) setDescriptionError('')
                }}
                rows={4}
                required
              />
              {descriptionError && <div className='text-danger small mt-1'>{descriptionError}</div>}
            </div>

            <div className='col-12'>
              <div className='alert alert-warning d-flex align-items-start mb-0'>
                <i className='bi bi-exclamation-triangle-fill fs-4 me-3 mt-1'></i>
                <div>
                  O estorno será aplicado sobre o cashback do pedido{' '}
                  <strong>{selectedTransaction.orderId}</strong>. A observação é obrigatória.
                </div>
              </div>
            </div>
          </div>
        )}

        {modalMode === 'create' && (
          <div className='row g-4'>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-search text-primary me-2'></i>
                Buscar pedido
              </h6>
              <Input
                inputAttr={{type: 'text'}}
                label='Número do pedido'
                placeholder='Digite ao menos 4 caracteres para buscar...'
                value={orderSearchQuery}
                change={(value: string) => {
                  setOrderSearchQuery(value)
                  if (selectedOrder) {
                    setSelectedOrder(null)
                    setBenefitValue(0)
                  }
                  if (createErrors.order) {
                    setCreateErrors((prev) => {
                      const next = {...prev}
                      delete next.order
                      return next
                    })
                  }
                }}
                disabled={!!selectedOrder}
              />
              <div className='text-muted small mt-1'>
                A busca é feita automaticamente pelo número do pedido.
              </div>
              {createErrors.order && (
                <div className='text-danger small mt-1'>{createErrors.order}</div>
              )}
            </div>

            {selectedOrder ? (
              <div className='col-12'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='fw-bold text-gray-800 mb-0'>Pedido selecionado</h6>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger'
                    onClick={() => {
                      setSelectedOrder(null)
                      setBenefitValue(0)
                      setCreateErrors({})
                    }}
                  >
                    Trocar pedido
                  </button>
                </div>
                <div className='border border-primary border-opacity-25 rounded-2 p-3 bg-light-primary'>
                  <div className='fw-bold text-gray-800'>#{selectedOrder.orderidExt}</div>
                  <div className='text-muted small mt-1'>Cliente: {selectedOrder.customerName}</div>
                  <div className='text-muted small'>Total: {selectedOrder.totalDisplay}</div>
                  <div className='text-muted small'>Status: {selectedOrder.statusName}</div>
                  <div className='text-muted small'>Data: {selectedOrder.created}</div>
                </div>
              </div>
            ) : (
              <div className='col-12'>
                <div className={`${styles.resultsScroll} border rounded-2 p-2 bg-light`}>
                  {orderSearchLoading ? (
                    <div className='text-center text-muted p-4'>
                      <div
                        className='spinner-border spinner-border-sm text-primary me-2'
                        role='status'
                      />
                      Buscando pedidos...
                    </div>
                  ) : orderSearchResults.length > 0 ? (
                    orderSearchResults.map((order) => (
                      <div
                        key={order.id}
                        className={`d-flex align-items-center gap-3 p-3 rounded-2 border border-secondary border-opacity-25 bg-white ${styles.resultItem} cursor-pointer mb-2`}
                        onClick={() => {
                          setSelectedOrder(order)
                          setOrderSearchResults([])
                          setCreateErrors({})
                        }}
                      >
                        <div className='symbol symbol-40px symbol-fixed flex-shrink-0'>
                          <div className='symbol-label bg-light-primary'>
                            <i className='bi bi-receipt text-primary fs-4'></i>
                          </div>
                        </div>
                        <div className='flex-grow-1' style={{minWidth: 0}}>
                          <div className='fw-bold text-gray-800 text-truncate'>
                            #{order.orderidExt}
                          </div>
                          <div className='text-muted small mt-1'>{order.customerName}</div>
                          <div className='d-flex flex-wrap gap-2 mt-1'>
                            <span className='badge badge-light-secondary'>{order.statusName}</span>
                            <span className='badge badge-light-success'>{order.totalDisplay}</span>
                          </div>
                        </div>
                        <span className='badge badge-light fw-normal flex-shrink-0'>
                          ID {order.id}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className='text-center text-muted p-4'>
                      <i className='bi bi-search fs-2 d-block mb-2'></i>
                      Digite ao menos dois caracteres para localizar o pedido.
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedOrder && (
              <>
                <div className='col-12'>
                  <div className='separator separator-dashed'></div>
                </div>

                <div className='col-12'>
                  <h6 className='fw-bold text-gray-800 mb-3'>
                    <i className='bi bi-percent text-primary me-2'></i>
                    Valor do cashback
                  </h6>
                  <div className='d-flex gap-3 mb-4'>
                    <Button
                      text='Percentual (%)'
                      color={benefitKind === 'percent' ? 'primary' : 'secondary'}
                      size='small'
                      click={() => {
                        setBenefitKind('percent')
                        setBenefitValue(0)
                        setCreateErrors({})
                      }}
                      btnClass='mb-0'
                    />
                    <Button
                      text='Valor (R$)'
                      color={benefitKind === 'fixed' ? 'primary' : 'secondary'}
                      size='small'
                      click={() => {
                        setBenefitKind('fixed')
                        setBenefitValue(0)
                        setCreateErrors({})
                      }}
                      btnClass='mb-0'
                    />
                  </div>
                </div>

                <div className='col-md-6'>
                  {benefitKind === 'percent' ? (
                    <Input
                      inputAttr={{type: 'number'}}
                      label='Cashback (%)'
                      value={String(benefitValue)}
                      change={(value: string) => {
                        setBenefitValue(parseFloat(value) || 0)
                        if (createErrors.benefitValue) {
                          setCreateErrors((prev) => {
                            const next = {...prev}
                            delete next.benefitValue
                            return next
                          })
                        }
                      }}
                      minNumber={0}
                      maxNumber={99}
                      allowFloat={true}
                      marginTop='0'
                    />
                  ) : (
                    <Input
                      inputAttr={{type: 'money'}}
                      label='Valor (R$)'
                      value={benefitValue}
                      change={(value: number) => {
                        setBenefitValue(value || 0)
                        if (createErrors.benefitValue) {
                          setCreateErrors((prev) => {
                            const next = {...prev}
                            delete next.benefitValue
                            return next
                          })
                        }
                      }}
                      currency='BRL'
                      locale='pt-BR'
                      marginTop='0'
                    />
                  )}
                  {createErrors.benefitValue && (
                    <div className='text-danger small mt-1'>{createErrors.benefitValue}</div>
                  )}
                </div>

                <div className='col-md-6'>
                  <div className='text-muted fs-7 mb-1'>Valor calculado</div>
                  <div className='fw-bold text-gray-800 fs-4'>
                    {formatMoney(getCalculatedAmount())}
                  </div>
                  <div className='text-muted small mt-1'>
                    Total do pedido: {selectedOrder.totalDisplay}
                  </div>
                </div>

                <div className='col-12'>
                  <Input
                    inputAttr={{type: 'textarea'}}
                    label='Descrição (opcional)'
                    placeholder='Informe uma descrição para esta movimentação'
                    value={createDescription}
                    change={setCreateDescription}
                    rows={3}
                    marginTop='0'
                  />
                </div>
              </>
            )}
          </div>
        )}
      </ModalWrapper>
    </>
  )
}

const CashbackList: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Cashback', path: 'cashback', isActive: false},
          {title: 'Movimentações', path: 'cashback/list', isActive: true},
        ]}
      >
        Movimentações
      </PageTitle>
      <CashbackListContent />
    </>
  )
}

export {CashbackList}
